// =============================================================================
// Copyright   : 2019 semantics GmbH
// Date        : $Date: 2026-01-14 17:43:36 +0100 (Wed, 14 Jan 2026) $
// Author      : Dirk Rothe, Tobias Schwarzkopf
// Worker      : $Author: d.rothe $
// Revision    : $Rev: 159808 $
// Purpose     : header components
// =============================================================================
// ---Third-party imports
import React from 'react'
import {Icon, Button, Header, Segment} from 'semantic-ui-react'

// ---Framework imports
import {isObject, isArray, isString, isNil, mapItems, dict, isBoolean} from 'ldash'
import log from 'uix/logging'
import {
  useState,
  useSelector,
  useUserSetting,
  useFullscreen,
  FullscreenContext,
  useRef,
  useEffect,
  usePartContext,
  useActions,
} from 'uix/hooks'
import {Sel} from 'uix/registries'
import {ucFirst} from 'uix/utils'

// ---Local imports
import {PortalTarget} from './portals'
import S from './header.scss'
import {VIcon} from 'uix/graphics'
import {XMenu} from './menu'
import {Toolbar} from './toolbar'
import {XPopup} from './popup'

import clsx from 'clsx'

const ToolbarWrap = ({toolbar, children}) => {
  let {titled, size, colored} = toolbar
  return toolbar ? (
    <Toolbar
      className={S.toolbar}
      titled={titled}
      size={size ? size : 'small'}
      colored={colored && colored}
    >
      {children}
    </Toolbar>
  ) : (
    <>{children}</>
  )
}

// ---Exports
export const HeaderSettingsIcon = ({
  header, // defaults to tabname
  subheader = null,
  icon = 'gem outline',
  menu = null, // add a menu
  bottomMenu = null,
  portal = null, // add a menu via portal id referencing
  fluidMenu = false, // menu grows to full header
  style = {display: 'flex', justifyContent: 'space-between', verticalAlign: 'top'},
  basic = false,
  onIconClick = null,
  children, // XXX: really used?
  fullscreenable = false,
  isFullscreen,
  toggleFullscreen,
  loading = false, // show a loading indicator as corner icon
  toolbar = true,
  compact = false, // shrink space usage
  iconPopup,
}) => {
  let {tabname} = useSelector(Sel.request)
  header = header || ucFirst(tabname)

  let i18n = useSelector(Sel.i18n)

  let [iconPopupOpen, setIconPopupOpen] = useState(false)
  if (iconPopup) {
    onIconClick = () => setIconPopupOpen(!iconPopupOpen)
  }

  let iconProps = {}
  if (onIconClick) {
    iconProps = {style: {cursor: 'pointer'}, onClick: onIconClick}
  }

  if (basic) {
    icon = null
  }

  if (loading) {
    if (isString(icon)) {
      icon = [icon]
    }
    if (isArray(icon)) {
      icon = [...icon, {name: 'sync', loading: true, color: 'green'}]
    }
  }

  if (isArray(icon)) {
    let [icon1, icon2, icon3] = icon
    let decode = i => (isString(i) ? {name: i} : {...i})
    icon = (
      <>
        <VIcon {...{...decode(icon1), ...iconProps}} />
        {icon2 && <VIcon corner="bottom right" {...decode(icon2)} />}
        {icon3 && <VIcon corner="bottom left" {...decode(icon3)} />}
      </>
    )
  } else if (isObject(icon)) {
    iconProps = {...icon, ...iconProps}
    icon = <Icon {...iconProps} />
  } else if (isString(icon)) {
    icon = <VIcon name={icon} {...iconProps} />
  } else if (!isNil(icon)) {
    log.warn('invalid icon format %o', icon)
  }
  let iconContent = (
    <span className={clsx(S.icon, compact && S.compact, iconPopupOpen && S.iconPopupOpen)}>
      <Icon.Group>{icon}</Icon.Group>
    </span>
  )
  if (iconPopup) {
    iconContent = (
      <XPopup
        open={iconPopupOpen}
        basic
        fitted
        hoverable
        trigger={iconContent}
        onClose={() => setIconPopupOpen(false)}
      >
        {iconPopup}
      </XPopup>
    )
  }
  return (
    <>
      <Header style={style} className={clsx(S.header, compact && S.compact)}>
        <div style={{display: 'flex', flexDirection: 'row', alignItems: 'center'}}>
          {children && <span className={S.content}>{children}</span>}
          {!basic ? iconContent : null}
          {!basic ? (
            <Header.Content>
              {header}
              {subheader && <Header.Subheader>{subheader}</Header.Subheader>}
            </Header.Content>
          ) : (
            <Header.Content as="h2">
              {header}
              {subheader && <Header.Subheader>{subheader}</Header.Subheader>}
            </Header.Content>
          )}
        </div>
        <div
          className={clsx(S.menu, compact && S.compact, fluidMenu && S.fluidMenu)}
          style={{display: 'flex'}}
        >
          <ToolbarWrap toolbar={toolbar}>
            {menu}
            {/* {toolbar && portal && <XMenu.Separator style={{display: 'block'}} />} */}
            {portal && <PortalTarget id={portal} />}
          </ToolbarWrap>
          {fullscreenable && (
            <Button
              secondary
              title={isFullscreen ? i18n.get('fullscreenClose') : i18n.get('fullscreenOpen')}
              onClick={() => toggleFullscreen()}
              style={{position: 'absolute', right: '0', top: '0'}}
              size="mini"
              icon={isFullscreen ? 'close' : 'expand'}
            />
          )}
        </div>
      </Header>
      {bottomMenu}
    </>
  )
}

const Fullscreen = ({isFullscreen, fRef, children, ...props}) => (
  <div ref={fRef} {...props}>
    <FullscreenContext.Provider value={{isFullscreen, ref: fRef}}>
      {isFullscreen && <PortalTarget id="fullscreenRootPortal" />}
      {children}
    </FullscreenContext.Provider>
  </div>
)

export const HeaderIconPartMenu = ({toggleFullscreen}) => {
  let {name: partName} = usePartContext()
  let clearPartPersistence = useActions('clearPartPersistence')

  let persistedUi = useSelector(root => root.ui.byId)
  // loop over all persisted query params and use stuff that has been modified
  let query = dict(
    mapItems(
      persistedUi,
      (name, data) => {
        let pname = name.split('/').at(-1)
        let {value, initialValue} = data
        if (isBoolean(value) && initialValue === '') {
          // toggle bools are initial ''
          initialValue = false
        }
        if (String(value) != String(initialValue)) {
          return [pname, value]
        }
      },
      name => name.startsWith(`Part/${partName}/query/`)
    ).filter(Boolean)
  )

  return (
    <XMenu vertical>
      <XMenu.Label content={partName} />
      <XMenu.Item icon="expand" content="Fullscreen" onClick={toggleFullscreen} />
      <XMenu.Item
        icon="configure"
        content="Clear"
        title="Clear local configuration"
        onClick={() => clearPartPersistence(partName)}
      />
      <XMenu.LinkItem
        query={query}
        icon="world"
        title="Build persistent link for local configuration"
        content="Link"
      />
    </XMenu>
  )
}

export const HeaderSegment = ({
  children,
  className,
  flex, // set flex css props on outer div
  height,
  overflow, // set overflow css prop on segment
  overflowInner = false, // set overflow css prop on children (non-header)
  overflowKey, // reset overflow scrollpos to 0 if change
  flexInner = false, // set flex css prop on children (non-header)
  embedded = false, // deletes border, boxshadow, and radius for better embed
  major, // major headerSegment that contains further segments - different styling
  attached,
  compact,
  secondary,
  fullscreenable = true, // enable fullscreen feature
  headerIconPartMenu = true,
  headerless = false, // do not draw header
  fitted = false, // padding 0
  style = {},
  externalRef,
  ...props
}) => {
  const {ref, toggleFullscreen, isFullscreen} = useFullscreen(externalRef)

  let [headerStyle, setHeaderStyle] = useUserSetting('headerStyle')

  // flex == true has two effects:
  // 1) grow this segment
  // 2) set flex-mode to column for children
  // 3) set overflow to hidden (children need to manage overflow now)
  if (flex) {
    style = {flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', ...style}
  }
  if (height) {
    style = {...style, height: `${height}px`}
  }

  let decode_ov = ov => (isString(ov) ? ov : 'auto')
  if (overflow) {
    style = {...style, overflow: decode_ov(overflow)}
  }
  let header = null
  if (!headerless) {
    if (headerStyle === 'compact') {
      props = {...props, compact: true}
    }
    if (fullscreenable && !headerIconPartMenu) {
      props = {...props, fullscreenable, isFullscreen, toggleFullscreen}
    }
    let iconPopup = headerIconPartMenu ? (
      <HeaderIconPartMenu toggleFullscreen={toggleFullscreen} />
    ) : null

    header = <HeaderSettingsIcon iconPopup={iconPopup} {...props} />
  }

  let raised = true
  if (major) {
    raised = false
    className = clsx(className, S.majorSegment)
  }

  let overflowRef = useRef()
  useEffect(() => {
    if (overflowKey && overflowRef.current) {
      overflowRef.current.scrollTop = 0
      //~ overflowRef.current.scrollIntoView({behavior: 'smooth'})
    }
  }, [overflowKey])
  if (overflowInner) {
    let f = flexInner
      ? {flex: 1, display: 'flex', flexDirection: isString(flexInner) ? flexInner : 'column'}
      : {}
    children = (
      <div ref={overflowRef} style={{overflow: decode_ov(overflowInner), ...f}}>
        {children}
      </div>
    )
  }

  let seg_props = {}

  if (fullscreenable) {
    seg_props = {...seg_props, as: Fullscreen, fRef: ref, isFullscreen}
  }

  if (fitted) {
    style = {...style, padding: 0}
  }
  if (embedded) {
    style = {...style, height: '100%', border: '0', borderRadius: '0', boxShadow: 'none'}
  }

  return (
    <Segment
      raised={raised}
      attached={attached}
      compact={compact}
      secondary={secondary}
      className={className}
      style={style}
      {...seg_props}
    >
      {header}
      {children}
    </Segment>
  )
}

export const TableSegment = ({children, ...props}) => (
  /* configure a HeaderSegment to play nice with a <SelectableTable flex/>
  flexInner="row" will position the inner table scrollbars correctly
   */
  <HeaderSegment major flex overflowInner flexInner="row" {...props}>
    {children}
  </HeaderSegment>
)
